import { useMemo, useState } from 'react';

import FilterBar from './ui/FilterBar.jsx';
import Button from './ui/Button.jsx';
import { applyFilters, facetsOf, rollup, isDirty, DIMENSIONS } from '../util/analyticsFilters.js';

// T328 — the visible half of the re-scope engine.
//
// The operator's complaint was "cannot filter": every card was served
// pre-aggregated, so the page could answer "by source" and "by score band" and
// never "by source AND by score band". T327 shipped the raw rows and
// util/analyticsFilters.js the arithmetic; this is the surface.
//
// Deliberately NOT a re-implementation of the whole page. The strip below
// recomputes the four numbers a row can honestly answer and says plainly that
// the cards underneath are still all-time — a filter that silently rescoped
// half a page and left the other half alone would be worse than none, because
// nothing on screen would say which half you were reading.

// How many chips a dimension shows before folding. 40 sources and 128 cities
// would bury the page; the tail is reachable, just not shouted.
const CHIP_LIMIT = 6;

function ChipGroup({ facet, selected, onToggle }) {
  const [expanded, setExpanded] = useState(false);
  // T349 — a selected chip is never folded away. The cap exists so 128 cities
  // do not bury the page, but a value ranked 7th by count that the operator has
  // actually selected has to stay reachable, or the only way to undo it is the
  // global Clear. Selected first, then the rest up to the cap.
  const chosen = facet.values.filter((v) => selected.has(v.value));
  const rest = facet.values.filter((v) => !selected.has(v.value));
  const values = expanded
    ? [...chosen, ...rest]
    : [...chosen, ...rest.slice(0, Math.max(0, CHIP_LIMIT - chosen.length))];
  const hidden = facet.values.length - values.length;
  if (!facet.values.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="mr-0.5 text-[10px] uppercase tracking-wide text-slate-500">{facet.label}</span>
      {values.map((v) => {
        const on = selected.has(v.value);
        return (
          <button
            key={v.value}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(facet.key, v.value)}
            className={
              'rounded-full border px-2 py-0.5 text-xs transition-colors '
              + (on
                ? 'border-brand-600 bg-brand-600/20 text-brand-400'
                : 'border-slate-700 bg-slate-900/60 text-slate-300 hover:border-slate-500')
            }
          >
            {v.value}
            <span className="ml-1 tabular-nums text-slate-500">{v.count}</span>
          </button>
        );
      })}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="rounded-full border border-dashed border-slate-700 px-2 py-0.5 text-xs text-slate-500 hover:text-slate-300"
        >
          +{hidden} more
        </button>
      )}
    </div>
  );
}

function Stat({ label, value, suffix = '' }) {
  return (
    <div className="min-w-[5.5rem]">
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="font-mono text-lg tabular-nums text-slate-100">
        {/* null is "nothing to divide by", which is not the same answer as 0%. */}
        {value == null ? <span className="text-slate-600">—</span> : `${value}${suffix}`}
      </p>
    </div>
  );
}

export default function AnalyticsScope({ rows, filters, setFilters }) {
  const facets = useMemo(() => facetsOf(rows ?? [], filters), [rows, filters]);
  const scoped = useMemo(() => applyFilters(rows ?? [], filters), [rows, filters]);
  const stats = useMemo(() => rollup(scoped), [scoped]);
  const dirty = isDirty(filters);

  if (!rows?.length) return null;

  const toggle = (key, value) => {
    const next = new Set(filters[key] ?? []);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setFilters({ [key]: next });
  };

  const reset = () => {
    const cleared = Object.fromEntries(DIMENSIONS.map((d) => [d.key, new Set()]));
    setFilters({ ...cleared, from: '', to: '' });
  };

  return (
    <section className="space-y-2">
      <FilterBar
        shown={scoped.length}
        total={rows.length}
        dirty={dirty}
        onReset={reset}
        noun="applications"
        className="flex-col items-stretch gap-2"
      >
        <div className="flex flex-col gap-1.5">
          {facets.map((f) => (
            <ChipGroup key={f.key} facet={f} selected={new Set(filters[f.key] ?? [])} onToggle={toggle} />
          ))}
        </div>
      </FilterBar>

      <div className="flex flex-wrap items-end gap-5 rounded-xl border border-slate-800 bg-slate-900/40 p-3">
        <Stat label="Applied" value={stats.applied} />
        <Stat label="Advanced" value={stats.advanced} />
        <Stat label="Reply rate" value={stats.replyRatePct} suffix="%" />
        <Stat label="Gone quiet" value={stats.ghosted} />
        {dirty && (
          <p className="ml-auto max-w-sm text-xs leading-snug text-slate-500">
            {/* Said out loud rather than left to be discovered: the cards below
                do not re-scope, because time-in-stage, cohorts and apply-lag
                need timestamps a row projection does not carry. */}
            These four follow your filter. The cards below still show every
            application — they need per-stage timings the filtered rows don&rsquo;t carry.
          </p>
        )}
      </div>

      {dirty && scoped.length === 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-center text-xs text-slate-400">
          Nothing matches all of those at once.
          <Button variant="secondary" size="sm" className="ml-2" onClick={reset}>Clear filters</Button>
        </div>
      )}
    </section>
  );
}
